/**
 * Right-rail summary for the flight checkout page. Reuses the flight UI
 * components used elsewhere (`FlightSummaryHeader`, `FlightSummary`) so the
 * shape and styling stay consistent with the chat widget and itinerary cards.
 */

import type { FlightOptionModel } from '@elara/client-core/models/FlightOptionModel';
import type { BookingDetails } from '@elara/domain/models/schemas/itinerary-item-schemas';

import React from 'react';

import { FlightSummary, FlightSummaryHeader } from '#ui/cards/FlightCard.js';

interface FlightBookingSummaryProps {
  flightOption: FlightOptionModel | undefined;
  booking: BookingDetails | undefined;
}

export function FlightBookingSummary({ flightOption, booking }: FlightBookingSummaryProps): React.ReactNode {
  if (!flightOption) {
    return <p className="text-sm text-muted-foreground">Loading flight…</p>;
  }

  const outboundSegments = flightOption.flightsForDirection('outbound');
  const returnSegments = flightOption.flightsForDirection('return');

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Outbound</h3>
        <div className="rounded-md border border-border bg-background p-4">
          <FlightSummaryHeader flightOption={flightOption} direction="outbound" className="mb-3" />
          {outboundSegments.length > 0 ? <FlightSummary segments={outboundSegments} /> : null}
        </div>
      </section>

      {flightOption.roundTrip && returnSegments.length > 0 ? (
        <section className="space-y-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Return</h3>
          <div className="rounded-md border border-border bg-background p-4">
            <FlightSummaryHeader flightOption={flightOption} direction="return" className="mb-3" />
            <FlightSummary segments={returnSegments} />
          </div>
        </section>
      ) : null}

      <section className="border-t border-border pt-4">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-muted-foreground">
            {booking?.status === 'booked' ? 'Total' : 'Estimated total'}
          </span>
          <span className="text-lg font-semibold">{flightOption.formattedTotalPrice}</span>
        </div>
      </section>
    </div>
  );
}
